import React from 'react';

type WtiLoaderSize = 'sm' | 'md' | 'lg';

interface WtiLoaderProps {
  /** Optional status text under the brand mark */
  message?: string;
  /** Centered full-viewport / full-section loading state */
  fullPage?: boolean;
  size?: WtiLoaderSize;
  className?: string;
}

const SIZE: Record<
  WtiLoaderSize,
  { icon: number; ring: number; letter: number; sub: number; msg: number; gap: number }
> = {
  sm: { icon: 36, ring: 52, letter: 22, sub: 10, msg: 12, gap: 10 },
  md: { icon: 56, ring: 80, letter: 32, sub: 12, msg: 14, gap: 14 },
  lg: { icon: 72, ring: 100, letter: 40, sub: 13, msg: 15, gap: 16 },
};

/**
 * Branded WTI loading indicator — pin logo + spinner ring.
 */
const WtiLoader: React.FC<WtiLoaderProps> = ({
  message,
  fullPage = false,
  size = 'md',
  className = '',
}) => {
  const s = SIZE[size];
  const logoSrc = `${process.env.PUBLIC_URL || ''}/images/logo.png`;

  return (
    <div
      className={`wti-loader${fullPage ? ' wti-loader--full' : ''} ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div
        className="wti-loader__icon-wrap"
        style={{ width: s.ring, height: s.ring }}
        aria-hidden="true"
      >
        <span className="wti-loader__ring" />
        <img
          src={logoSrc}
          alt=""
          className="wti-loader__icon"
          style={{ width: s.icon, height: s.icon }}
        />
      </div>

      <div className="wti-loader__brand" style={{ gap: Math.max(4, s.gap / 2) }}>
        <div className="wti-loader__row" style={{ fontSize: s.letter }}>
          <span className="wti-loader__letter">w</span>
          <span className="wti-loader__letter">t</span>
          <span className="wti-loader__letter">i</span>
        </div>
        <div className="wti-loader__sub" style={{ fontSize: s.sub }}>
          where to invest
        </div>
      </div>

      {message ? (
        <p className="wti-loader__message" style={{ fontSize: s.msg }}>
          {message}
        </p>
      ) : (
        <span className="sr-only">Loading</span>
      )}
    </div>
  );
};

export default WtiLoader;
